import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarClock, Download, Users } from "lucide-react";
import AppLayout from "@/components/app-layout";
import { AdminAccessGuard } from "@/components/admin-access-guard";
import { usePageTitle } from "@/hooks/use-page-title";
import { CONFERENCE_STAGE_LABELS, type ConferenceLead, type ConferenceStage } from "@shared/schema";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

function csvCell(value: string | number | null | undefined): string {
  let str = value == null ? "" : String(value);
  // Neutralize spreadsheet formula injection: prefix risky leading chars so
  // Excel/Sheets treat the cell as text rather than executing a formula.
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildConferenceLeadsCsv(leads: ConferenceLead[]): string {
  const headers = [
    "Join #",
    "Name",
    "Email",
    "PadSplit",
    "Stage",
    "Rooms",
    "Submitted At",
    "Account Created",
  ];
  const rows = leads.map((lead) => [
    csvCell(lead.joinOrder),
    csvCell(lead.name),
    csvCell(lead.email),
    csvCell(lead.padsplitUser ? "Y" : "N"),
    csvCell(CONFERENCE_STAGE_LABELS[lead.colivingStage as ConferenceStage] ?? lead.colivingStage),
    csvCell(lead.roomCount ?? ""),
    csvCell(new Date(lead.createdAt).toISOString()),
    csvCell(lead.userId != null ? "Y" : "N"),
  ]);
  return [headers, ...rows].map((row) => row.join(",")).join("\r\n");
}

export default function AppAdminConferenceLeads() {
  usePageTitle("Conference Leads");

  const leadsQuery = useQuery<ConferenceLead[]>({
    queryKey: ["/api/app/admin/conference-leads"],
  });
  const leads = leadsQuery.data ?? [];
  const convertedCount = leads.filter((l) => l.userId != null).length;

  const handleExportCsv = () => {
    const csv = buildConferenceLeadsCsv(leads);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `conference-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <AdminAccessGuard error={leadsQuery.error}>
        <div className="p-6 space-y-4 max-w-6xl" data-tour="admin-conference-leads-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-semibold" data-testid="heading-conference-leads">Conference Leads</h1>
              <p className="text-sm text-muted-foreground">
                Everyone who started the co-living conference signup flow, in join order.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-2xl font-semibold tabular-nums" data-testid="text-conference-total">{leads.length}</div>
                <div className="text-xs text-muted-foreground">Total leads</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold tabular-nums text-brand" data-testid="text-conference-converted">{convertedCount}</div>
                <div className="text-xs text-muted-foreground">Created account</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCsv}
                disabled={leads.length === 0}
                data-testid="button-export-conference-leads"
              >
                <Download className="h-4 w-4" /> Export CSV
              </Button>
              <GuideButton pageKey="admin-conference-leads" compactOnMobile />
            </div>
          </div>
          <GuideNudgeBanner pageKey="admin-conference-leads" />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" /> Signups
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {leadsQuery.isLoading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : leads.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="text-conference-empty">
                  No conference leads yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="px-4 py-2.5 font-medium">#</th>
                        <th className="px-4 py-2.5 font-medium">Name</th>
                        <th className="px-4 py-2.5 font-medium">Email</th>
                        <th className="px-4 py-2.5 font-medium">PadSplit</th>
                        <th className="px-4 py-2.5 font-medium">Stage</th>
                        <th className="px-4 py-2.5 font-medium text-right">Rooms</th>
                        <th className="px-4 py-2.5 font-medium">Account</th>
                        <th className="px-4 py-2.5 font-medium">Submitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-accent/50" data-testid={`row-conference-lead-${lead.id}`}>
                          <td className="px-4 py-2.5 font-mono text-muted-foreground tabular-nums" data-testid={`text-join-order-${lead.id}`}>
                            {lead.joinOrder}
                          </td>
                          <td className="px-4 py-2.5 font-medium" data-testid={`text-lead-name-${lead.id}`}>{lead.name}</td>
                          <td className="px-4 py-2.5 text-muted-foreground" data-testid={`text-lead-email-${lead.id}`}>{lead.email}</td>
                          <td className="px-4 py-2.5">
                            {lead.padsplitUser ? (
                              <Badge variant="default" className="text-[10px] px-1.5 py-0">Yes</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">No</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5" data-testid={`text-lead-stage-${lead.id}`}>
                            {CONFERENCE_STAGE_LABELS[lead.colivingStage as ConferenceStage] ?? lead.colivingStage}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`text-lead-rooms-${lead.id}`}>
                            {lead.roomCount ?? "—"}
                          </td>
                          <td className="px-4 py-2.5">
                            {lead.userId != null ? (
                              <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-brand text-brand-foreground">Created</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">Lead only</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <CalendarClock className="h-3 w-3" />
                              {new Date(lead.createdAt).toLocaleDateString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </AdminAccessGuard>
    </AppLayout>
  );
}
